/**
 * Tarjeta de una tarea en el tablero.
 *
 * Se puede arrastrar a otra columna (con el mouse) o mover con los botones ← y → (con teclado o
 * en pantallas táctiles) dentro del flujo; una descartada no tiene ← → (se reabre desde la ficha).
 * Al hacer clic se abre el detalle. Las cerradas llevan su marca («Hecha» / «Descartada») y la
 * descartada, además, el título tachado y el motivo debajo. Como máximo una línea de relación (madre o
 * subtarea) y una marca de bloqueada, con el borde izquierdo ámbar (ADR-0018). Si tiene fecha límite y sigue
 * abierta, una marca «Vence 12 nov» (ámbar si vence pronto, roja si está atrasada; CDT-76).
 */
import {
  bloqueantesAbiertos,
  ESTADOS_DEL_FLUJO,
  ETIQUETA_ESFUERZO,
  ETIQUETA_ESTADO,
  esEstadoCerrado,
  formatearNumero,
  hoyEnChile,
  textoMotivoDescarte,
  type Estado,
  type EstadoDelFlujo,
  type Tarea,
} from '@cdtec-backlog/contrato';
import { Avatar, nombreCorto } from '../../componentes/Avatar';
import { Giro } from '../../componentes/Esqueleto';
import { Icono } from '../../componentes/Icono';
import { fechaCorta } from '../../lib/formato';
import { EtiquetasTarea } from './EtiquetasTarea';
import { MarcaFechaLimite } from './FechaLimite';
import { IconoTipo } from './IconoTipo';
import { MarcaCierre } from './MarcaCierre';
import { BarraProgreso } from './Subtareas';
import './Bloqueos.css';

interface Props {
  tarea: Tarea;
  puedeEscribir: boolean;
  /** Movida en pantalla y esperando al servidor: atenuada, borde punteado y un giro chico. */
  moviendo?: boolean;
  /** Día de hoy en Chile (`AAAA-MM-DD`) para marcar la fecha límite. Por defecto, ahora (las pruebas lo fijan). */
  hoy?: string;
  onAbrir: (id: number) => void;
  onMover: (id: number, estado: Estado) => void;
}

/**
 * Una sola línea de relación (ADR-0018): la madre muestra el avance de sus subtareas y la hija de qué tarea
 * es parte (cortado con «…»). Un solo nivel: una tarea no es las dos cosas.
 */
function RelacionTarjeta({ tarea }: { tarea: Tarea }) {
  const progreso = tarea.progresoSubtareas;
  if (progreso && progreso.total > 0) {
    return (
      <p className="tarjeta__relacion tarjeta__progreso">
        <BarraProgreso progreso={progreso} />
        <span>
          Subtareas {progreso.hechas}/{progreso.total}
        </span>
      </p>
    );
  }
  if (tarea.padre) {
    const texto = `Parte de ${formatearNumero(tarea.padre.numero)} · ${tarea.padre.titulo}`;
    return (
      <p className="tarjeta__relacion">
        <Icono nombre="rama" tamano={13} />
        <span title={texto}>{texto}</span>
      </p>
    );
  }
  return null;
}

/**
 * Marca «Bloqueada por CDT-100, CDT-7» (ADR-0018) mientras la tarea esté bloqueada: solo los bloqueantes
 * abiertos (los cerrados ya no bloquean). Una línea; si son muchos, se corta con «…» y el título la completa.
 */
function MarcaBloqueo({ tarea }: { tarea: Tarea }) {
  if (!tarea.bloqueada) return null;
  const abiertos = bloqueantesAbiertos(tarea.bloqueadaPor);
  // `bloqueada` lo calcula la API; la lista de abiertos podría ir un paso atrás (p. ej. tras un movimiento
  // optimista del bloqueante): sin números, la marca dice solo «Bloqueada».
  const texto =
    abiertos.length > 0
      ? `Bloqueada por ${abiertos.map((b) => formatearNumero(b.numero)).join(', ')}`
      : 'Bloqueada';
  return (
    <p className="tarjeta__bloqueo">
      <span className="marca-bloqueo" title={texto}>
        <Icono nombre="candado" tamano={12} />
        <span>{texto}</span>
      </span>
    </p>
  );
}

export function TarjetaTarea({
  tarea,
  puedeEscribir,
  moviendo = false,
  hoy = hoyEnChile(),
  onAbrir,
  onMover,
}: Props) {
  // Una descartada no está en el flujo: -1, sin vecinos.
  const posicion = ESTADOS_DEL_FLUJO.indexOf(tarea.estado as EstadoDelFlujo);
  const anterior = posicion > 0 ? ESTADOS_DEL_FLUJO[posicion - 1] : undefined;
  const siguiente =
    posicion >= 0 && posicion < ESTADOS_DEL_FLUJO.length - 1
      ? ESTADOS_DEL_FLUJO[posicion + 1]
      : undefined;
  const descartada = tarea.estado === 'DESCARTADA';

  return (
    <article
      className={`tarjeta${descartada ? ' tarjeta--descartada' : ''}${moviendo ? ' tarjeta--moviendo' : ''}${tarea.bloqueada ? ' tarjeta--bloqueada' : ''}`}
      aria-busy={moviendo || undefined}
      data-tarea={tarea.id}
      draggable={puedeEscribir}
      onDragStart={(evento) => {
        evento.dataTransfer.setData('text/plain', String(tarea.id));
        evento.dataTransfer.effectAllowed = 'move';
      }}
    >
      <button type="button" className="tarjeta__abrir" onClick={() => onAbrir(tarea.id)}>
        <span className="tarjeta__tope">
          <span className="tarjeta__numero">
            <IconoTipo tipo={tarea.tipo} />
            {formatearNumero(tarea.numero)}
          </span>
          {moviendo && <Giro />}
          {tarea.adjuntos > 0 && (
            <span className="tarjeta__clip" title={`${tarea.adjuntos} adjuntos`}>
              <Icono nombre="clip" tamano={12} />
              {tarea.adjuntos}
            </span>
          )}
          {esEstadoCerrado(tarea.estado) ? (
            <MarcaCierre estado={tarea.estado} />
          ) : (
            // Se oculta con la preferencia «Esfuerzo en las tarjetas: Ocultar» (regla en Tablero.css).
            <span
              className="etiqueta-esfuerzo"
              title={`Esfuerzo: ${ETIQUETA_ESFUERZO[tarea.esfuerzo]}`}
            >
              {ETIQUETA_ESFUERZO[tarea.esfuerzo]}
            </span>
          )}
        </span>
        <span className="tarjeta__titulo">{tarea.titulo}</span>
      </button>
      <RelacionTarjeta tarea={tarea} />
      <MarcaBloqueo tarea={tarea} />
      {/* En una cerrada la fecha límite ya no importa: no se muestra en el tablero (sí en la lista y la ficha). */}
      {tarea.fechaLimite && !esEstadoCerrado(tarea.estado) && (
        <p className="tarjeta__limite">
          <MarcaFechaLimite tarea={tarea} hoy={hoy} />
        </p>
      )}
      {tarea.descarte && <p className="tarjeta__motivo">{textoMotivoDescarte(tarea.descarte)}</p>}
      <EtiquetasTarea tarea={tarea} conTipo={false} />
      <div className="tarjeta__pie">
        <span className="tarjeta__persona">
          {tarea.responsable && <Avatar nombre={tarea.responsable.nombre} />}
          <span className="tarjeta__nombre" title={tarea.responsable?.nombre}>
            {tarea.responsable ? nombreCorto(tarea.responsable.nombre) : 'Sin asignar'}
          </span>
        </span>
        <span className="tarjeta__fecha">{fechaCorta(tarea.actualizadoEn)}</span>
        {puedeEscribir && (
          <span className="tarjeta__mover">
            {anterior && (
              <button
                type="button"
                aria-label={`Mover ${formatearNumero(tarea.numero)} a ${ETIQUETA_ESTADO[anterior]}`}
                title={`Mover a ${ETIQUETA_ESTADO[anterior]}`}
                onClick={() => onMover(tarea.id, anterior)}
              >
                <Icono nombre="izq" tamano={12} />
              </button>
            )}
            {siguiente && (
              <button
                type="button"
                aria-label={`Mover ${formatearNumero(tarea.numero)} a ${ETIQUETA_ESTADO[siguiente]}`}
                title={`Mover a ${ETIQUETA_ESTADO[siguiente]}`}
                onClick={() => onMover(tarea.id, siguiente)}
              >
                <Icono nombre="der" tamano={12} />
              </button>
            )}
          </span>
        )}
      </div>
    </article>
  );
}
